import React, { useEffect, useMemo, useState } from "react";
import { Receipt, DownloadSimple, Calendar, Lightning, Bank, CreditCard, Money } from "@phosphor-icons/react";
import api, { formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { generatePaymentSummaryPDF } from "../../lib/paymentSummaryPdf";
import { generatePaymentBatchPDF } from "../../lib/paymentReceipt";

const fmtARS = (n) => `$${(Number(n) || 0).toLocaleString("es-AR")}`;

const METHOD_LABEL = {
  transferencia: "Transferencia",
  tarjeta_debito: "Débito",
  mercado_pago: "Mercado Pago",
  efectivo: "Efectivo",
};

const METHOD_COLOR = {
  transferencia: "#32D74B",
  tarjeta_debito: "#5E5CE6",
  mercado_pago: "#009EE3",
  efectivo: "#FFD60A",
};

const MES_LARGO = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function formatMonth(ym) {
  if (!ym || ym.length < 7) return ym;
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  if (idx < 0 || idx > 11) return ym;
  return `${MES_LARGO[idx]} ${y}`;
}

export default function PaymentsSummaryTab({ userId, targetUser, isAdmin }) {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [downloadingBatch, setDownloadingBatch] = useState(false);
  const [yearFilter, setYearFilter] = useState("all");

  useEffect(() => {
    setLoading(true);
    api.get(`/users/${userId}/payments/summary`)
      .then(({ data }) => setSummary(data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, [userId]);

  const years = useMemo(() => {
    if (!summary?.by_month) return [];
    const ySet = new Set(summary.by_month.map((m) => m.month.slice(0, 4)));
    return ["all", ...Array.from(ySet).sort().reverse()];
  }, [summary]);

  const filteredMonths = useMemo(() => {
    if (!summary?.by_month) return [];
    if (yearFilter === "all") return summary.by_month;
    return summary.by_month.filter((m) => m.month.startsWith(yearFilter));
  }, [summary, yearFilter]);

  const handleDownload = async () => {
    if (!summary) return;
    setDownloading(true);
    try {
      await generatePaymentSummaryPDF(summary, { yearFilter });
      toast.success("PDF generado");
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el PDF");
    } finally {
      setDownloading(false);
    }
  };

  const handleDownloadReceipts = async () => {
    // Trae los pagos completos (con datos bancarios/QR) para armar el PDF consolidado
    setDownloadingBatch(true);
    try {
      const { data } = await api.get(`/users/${userId}/payments`);
      const effective = (data || []).filter((p) => {
        const status = p.status || "registrado";
        if (!["aprobado", "registrado"].includes(status)) return false;
        if (yearFilter === "all") return true;
        const y = (p.payment_date || p.created_at || "").slice(0, 4);
        return y === yearFilter;
      });
      if (effective.length === 0) {
        toast.error("No hay comprobantes efectivos para el período seleccionado");
        return;
      }
      const title = yearFilter === "all" ? "Historial" : `Año_${yearFilter}`;
      await generatePaymentBatchPDF(effective, targetUser || summary.user, { title });
      toast.success(`PDF consolidado con ${effective.length} comprobante${effective.length === 1 ? "" : "s"}`);
    } catch (err) {
      console.error(err);
      toast.error("No se pudo generar el PDF de comprobantes");
    } finally {
      setDownloadingBatch(false);
    }
  };

  if (loading) return <div className="text-muted py-12 text-center">Cargando resumen...</div>;
  if (!summary) return <div className="text-muted py-12 text-center">Sin datos disponibles.</div>;

  const t = summary.totals || {};
  const methods = Object.entries(summary.by_method || {});

  return (
    <div className="space-y-6" data-testid="payments-summary-tab">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted">Histórico de pagos</div>
          <h2 className="font-display font-black uppercase text-2xl tracking-tighter">Resumen anual / mensual</h2>
          {isAdmin && (
            <div className="text-xs text-muted mt-1">
              Atleta: <span className="text-white font-semibold">{summary.user?.name}</span>
              {summary.user?.monthly_fee ? (
                <> · Cuota mensual: <span className="text-primary-app font-semibold">{fmtARS(summary.user.monthly_fee)}</span></>
              ) : null}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {years.length > 1 && (
            <select
              className="input-base !w-auto !py-2 text-sm"
              value={yearFilter}
              onChange={(e) => setYearFilter(e.target.value)}
              data-testid="summary-year-filter"
            >
              {years.map((y) => (
                <option key={y} value={y}>{y === "all" ? "Todos los años" : y}</option>
              ))}
            </select>
          )}
          <button
            onClick={handleDownload}
            disabled={downloading || !summary}
            className="btn-primary !py-2 !px-4 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="download-summary-pdf"
          >
            <DownloadSimple size={16} weight="bold" />
            {downloading ? "Generando..." : "Descargar PDF"}
          </button>
          <button
            onClick={handleDownloadReceipts}
            disabled={downloadingBatch || !summary || (summary.totals?.paid_count || 0) === 0}
            className="btn-secondary !py-2 !px-4 text-sm flex items-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="download-batch-receipts"
            title="Un solo PDF con todos los comprobantes del atleta (uno por página)"
          >
            <Receipt size={16} weight="bold" />
            {downloadingBatch ? "Generando..." : "Descargar comprobantes"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="summary-totals">
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Total pagado</div>
          <div className="font-display font-black text-2xl text-primary-app mt-1">{fmtARS(t.paid_total)}</div>
          <div className="text-[10px] text-muted mt-1">{t.paid_count || 0} comprobantes</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Pendientes</div>
          <div className="font-display font-black text-2xl mt-1" style={{ color: t.pending_count ? "#FFD60A" : undefined }}>
            {t.pending_count || 0}
          </div>
          <div className="text-[10px] text-muted mt-1">{fmtARS(t.pending_total)}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Primer pago</div>
          <div className="font-display font-black text-sm mt-1">{t.first_payment_date || "—"}</div>
        </div>
        <div className="bg-[#0A0A0A] border border-app p-4">
          <div className="text-[10px] uppercase tracking-widest text-muted">Último pago</div>
          <div className="font-display font-black text-sm mt-1">{t.last_payment_date || "—"}</div>
        </div>
      </div>

      {methods.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-widest text-muted mb-3">Total por método</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {methods.map(([m, data]) => {
              const Icon = m === "transferencia" ? Bank : m === "tarjeta_debito" ? CreditCard : m === "mercado_pago" ? Lightning : Money;
              return (
                <div key={m} className="bg-[#0A0A0A] border border-app p-4" data-testid={`summary-method-${m}`}>
                  <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-muted">
                    <Icon size={11} weight="bold" /> {METHOD_LABEL[m] || m}
                  </div>
                  <div className="font-display font-black text-xl mt-1" style={{ color: METHOD_COLOR[m] || "#FFFFFF" }}>
                    {fmtARS(data.total)}
                  </div>
                  <div className="text-[10px] text-muted">{data.count} pagos</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div>
        <div className="text-xs uppercase tracking-widest text-muted mb-3 flex items-center gap-2">
          <Calendar size={12} weight="bold" /> Detalle por mes
        </div>
        {filteredMonths.length === 0 ? (
          <div className="text-muted text-sm border border-app p-4 text-center">
            No hay pagos {yearFilter !== "all" ? `en ${yearFilter}` : "registrados"} todavía.
          </div>
        ) : (
          <div className="space-y-3" data-testid="summary-months-list">
            {filteredMonths.map((m) => (
              <div key={m.month} className="bg-[#0A0A0A] border border-app p-4" data-testid={`summary-month-${m.month}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-display font-black uppercase tracking-tight">{formatMonth(m.month)}</div>
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] uppercase tracking-widest text-muted">{m.count} pagos</span>
                    <span className="font-display font-black text-lg text-primary-app">{fmtARS(m.total)}</span>
                  </div>
                </div>
                <div className="divide-y divide-[#1a1a1a]">
                  {m.payments.map((p) => (
                    <div key={p.payment_id} className="py-2 flex items-center justify-between text-sm">
                      <div className="flex-1">
                        <div className="font-mono text-xs text-muted">{p.receipt_number}</div>
                        <div className="text-xs text-muted">
                          {p.payment_date} · {METHOD_LABEL[p.method] || p.method}
                          {p.concept ? ` · ${p.concept}` : ""}
                        </div>
                      </div>
                      <div className="font-display font-bold">{fmtARS(p.amount)}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-muted text-center pt-4">
        Generado: {new Date(summary.generated_at).toLocaleString("es-AR")}
      </div>
    </div>
  );
}
